import { useEffect, useState, useMemo } from 'react';
import './css/Escola.css';
import Layout from './components/Layout';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { fndeService } from './api/fndeService';

function SaldoFnde() {
  const [dados, setDados] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  
  // Estado para mês selecionado
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;
  const ano = localStorage.getItem('ano') || anoAtual.toString();
  const maxMonth = Number(ano) === anoAtual ? mesAtual - 1 : 12;
  const [mesSelecionado, setMesSelecionado] = useState(String(maxMonth > 0 ? maxMonth : 12).padStart(2, '0'));
  
  const escola = JSON.parse(localStorage.getItem('escola') || '{}');
  const cnpj = escola?.cnpj || '';
  const mes = `${mesSelecionado}-${ano}`;
  const mesAtivo = mes;
  const token = localStorage.getItem('token');
  const vinculo = JSON.parse(localStorage.getItem('vinculo') || '{}');
  const isAdmin = vinculo?.tipo === 'ADMIN';
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  function getContaLabel(linha) {
    return (linha['Conta'] || linha['Número Conta'] || linha['Num Conta'] || '').toString();
  }

  function getContaSortKey(linha) {
    const label = (getContaLabel(linha) || '').toString();
    const parts = label.split('-');
    const prefix = (parts[0] || '').trim();
    const suffix = (parts[1] || '').trim();
    const num = parseInt(prefix.replace(/\D/g, ''), 10);
    return {
      num: Number.isNaN(num) ? Number.MAX_SAFE_INTEGER : num,
      suffix: (suffix || '').toLowerCase(),
      label
    };
  }

  const sortedDados = useMemo(() => {
    const copy = Array.isArray(dados) ? [...dados] : [];
    copy.sort((a, b) => {
      const ka = getContaSortKey(a);
      const kb = getContaSortKey(b);
      if (ka.num !== kb.num) return ka.num - kb.num;
      const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
      if (s !== 0) return s;
      return ka.label.localeCompare(kb.label, 'pt-BR', { sensitivity: 'base' });
    });
    return copy;
  }, [dados]);

  const saldoBRL = v => {
    if (!v) return 0;
    return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0;
  };

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(dados) ? dados : []).forEach(linha => {
      const chave = String(linha['Descrição Programa FNDE'] || linha['Programa'] || 'Programa');
      if (!map[chave]) {
        map[chave] = { nome: chave, rdb: 0, poupanca: 0, fundos: 0, conta: 0, qtd: 0 };
      }
      map[chave].rdb += saldoBRL(linha['Saldo RDB/CDB']);
      map[chave].poupanca += saldoBRL(linha['Saldo Poupança']);
      map[chave].fundos += saldoBRL(linha['Saldo Fundos']);
      map[chave].conta += saldoBRL(linha['Saldo Conta']);
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [dados]);

  const fmtBRL = value => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  useEffect(() => {
    if (!cnpj) {
      showDefaultModal('warning', 'CNPJ não encontrado. Selecione uma escola.');
      return;
    }
    consultar();
  }, [mesAtivo]);

  const consultar = async () => {
    setLoading(true);
    setConsultaRealizada(true);
    try {
      const response = await fndeService.buscarSaldoEntidade(mes, cnpj);
      if (response.data.sucesso) {
        setDados(response.data.dados || []);
      } else {
        showDefaultModal('error', `Erro: ${response.data.erro}`);
        setDados([]);
      }
    } catch (error) {
      showDefaultModal('error', `Erro ao consultar saldo FNDE: ${error.message}`);
      setDados([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <DefaultModal {...setDefaultModal} />
      <ResponsiveShadow>
        <PageHeader
          title="Saldos FNDE"
          subtitle="Saldo das Entidades - Dados bancários por conta no PDDE"
          icon="bi-bank"
        />

        <PageContent style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Filtro de Mês */}
          <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
            <label className="form-label mb-0" style={{ minWidth: '80px' }}>
              <strong>Mês:</strong>
            </label>
            <div className="d-flex flex-wrap gap-1">
              {Array.from({ length: maxMonth }, (_, i) => i + 1).map(m => (
                <button
                  key={m}
                  type="button"
                  className={`btn btn-sm ${mesSelecionado === String(m).padStart(2, '0') ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => setMesSelecionado(String(m).padStart(2, '0'))}
                  disabled={isLoading}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
            <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>Ano: <strong>{ano}</strong></span>
            {dados.length > 0 && (
              <span className="text-muted ms-2" style={{ fontSize: '0.9rem' }}>
                | <strong>{dados.length}</strong> registro(s) | Mês: <strong>{mes}</strong> | CNPJ: <strong>{cnpj}</strong>
              </span>
            )}
          </div>

          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.nome} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.nome}>{prog.nome}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">RDB/CDB</span><span>{fmtBRL(prog.rdb)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Poupança</span><span>{fmtBRL(prog.poupanca)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Fundos</span><span>{fmtBRL(prog.fundos)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Conta</span><span>{fmtBRL(prog.conta)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{fmtBRL(prog.rdb + prog.poupanca + prog.fundos + prog.conta)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tabela de Resultados */}
          {consultaRealizada && (
            <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
              {dados.length > 0 ? (
                <table className="table table-striped table-hover table-sm" style={{ fontSize: '0.75rem' }}>
                  <thead className="table-success">
                    <tr>
                      <th>Descrição Programa FNDE</th>
                      <th>Banco</th>
                      <th>Agência</th>
                      <th>Conta</th>
                      <th>Saldo RDB/CDB</th>
                      <th>Saldo Poupança</th>
                      <th>Saldo Fundos</th>
                      <th>Saldo Conta</th>
                      <th>Valor Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDados.map((linha, index) => {
                      const parseBRL = v => {
                        if (!v) return 0;
                        return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0;
                      };
                      const total = [
                        parseBRL(linha['Saldo RDB/CDB']),
                        parseBRL(linha['Saldo Poupança']),
                        parseBRL(linha['Saldo Fundos']),
                        parseBRL(linha['Saldo Conta'])
                      ].reduce((a, b) => a + b, 0);
                      return (
                        <tr key={index}>
                          <td>{linha['Descrição Programa FNDE'] || linha['Programa'] || '-'}</td>
                          <td>{linha['Banco'] || '-'}</td>
                          <td>{linha['Agência'] || '-'}</td>                          <td>{linha['Conta'] || linha['Número Conta'] || linha['Num Conta'] || '-'}</td>                          <td className="text-end">{linha['Saldo RDB/CDB'] || '-'}</td>
                          <td className="text-end">{linha['Saldo Poupan a'] || '-'}</td>
                          <td className="text-end">{linha['Saldo Fundos'] || '-'}</td>
                          <td className="text-end">{linha['Saldo Conta'] || '-'}</td>
                          <td className="fw-bold text-success text-end">{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="alert alert-info" role="alert">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  Nenhum registro encontrado para o mês <strong>{mes}</strong> e CNPJ informado.
                  <br />
                  <small className="text-muted mt-2 d-block">
                    Verifique se o CNPJ possui registros no PDDE para este período.
                  </small>
                </div>
              )}
            </div>
          )}

        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default SaldoFnde;
